import { useState, useCallback } from "react";
import { useGraphQLFetch } from "./use-graphql-fetch";
import { toast } from "sonner";

// Types matching the backend GraphQL schema
export interface WarehouseOutput {
  warehouseID: number;
  warehouseCode: string;
  warehouseName: string;
  address: string | null;
  warehouseType: string;
  totalCapacity: number | null;
  currentUtilization: number | null;
  defaultReceiveLocation: string | null;
  defaultPickLocation: string | null;
  active: boolean;
  totalLocations: number;
  activeLocations: number;
  totalCurrentStock: number;
  createdAt: string;
  modifiedAt: string;
}

export interface LocationOutput {
  locationID: number;
  locationCode: string;
  warehouseCode: string;
  warehouseName: string;
  aisle: string | null;
  rack: string | null;
  shelf: string | null;
  bin: string | null;
  locationType: string;
  capacity: number | null;
  currentStock: number;
  utilizationPercent: number;
  pickingSequence: number | null;
  active: boolean;
  createdAt: string;
  modifiedAt: string;
}

// Frontend types (matching existing mock data types)
export interface Warehouse {
  id?: number;
  code: string;
  name: string;
  address: string;
  type: "Main" | "Satellite" | "Retail" | "Storage" | "Distribution";
  totalCapacity?: number;
  currentUtilization: number;
  active: boolean;
  defaultReceiveLocation?: string;
  defaultPickLocation?: string;
  totalLocations?: number;
  activeLocations?: number;
  totalCurrentStock?: number;
}

export interface Location {
  id?: number;
  locationCode: string;
  warehouseCode: string;
  aisle?: string;
  rack?: string;
  shelf?: string;
  bin?: string;
  locationType: "Storage" | "Picking" | "Receiving" | "Staging" | "Shipping" | "Quarantine";
  capacity?: number;
  currentStock: number;
  utilizationPercent: number;
  pickingSequence?: number;
  active: boolean;
}

// Pagination info from GraphQL connection
export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

// GraphQL response types
interface WarehouseConnection {
  items: WarehouseOutput[];
  pageInfo: PageInfo;
  totalCount?: number;
}

interface LocationConnection {
  items: LocationOutput[];
  pageInfo: PageInfo;
  totalCount?: number;
}

interface WarehousesGraphQLResponse {
  data?: {
    warehouses?: {
      warehouseList?: WarehouseConnection;
    };
  };
  errors?: Array<{ message: string }>;
}

interface LocationsGraphQLResponse {
  data?: {
    locations?: {
      locationList?: LocationConnection;
    };
  };
  errors?: Array<{ message: string }>;
}

// Map backend warehouse to frontend type
const mapWarehouseToFrontend = (node: WarehouseOutput): Warehouse => {
  return {
    id: node.warehouseID,
    code: node.warehouseCode,
    name: node.warehouseName,
    address: node.address || "",
    type: (node.warehouseType as Warehouse["type"]) || "Main",
    totalCapacity: node.totalCapacity || undefined,
    currentUtilization: node.currentUtilization || 0,
    active: node.active,
    defaultReceiveLocation: node.defaultReceiveLocation || undefined,
    defaultPickLocation: node.defaultPickLocation || undefined,
    totalLocations: node.totalLocations,
    activeLocations: node.activeLocations,
    totalCurrentStock: node.totalCurrentStock,
  };
};

// Map backend location to frontend type
const mapLocationToFrontend = (node: LocationOutput): Location => {
  return {
    id: node.locationID,
    locationCode: node.locationCode,
    warehouseCode: node.warehouseCode,
    aisle: node.aisle || undefined,
    rack: node.rack || undefined,
    shelf: node.shelf || undefined,
    bin: node.bin || undefined,
    locationType: (node.locationType as Location["locationType"]) || "Storage",
    capacity: node.capacity || undefined,
    currentStock: node.currentStock,
    utilizationPercent: node.utilizationPercent,
    pickingSequence: node.pickingSequence || undefined,
    active: node.active,
  };
};

// GraphQL query for warehouses
const WAREHOUSES_QUERY = `
  query GetWarehouses($first: Int, $after: String) {
    warehouses {
      warehouseList(first: $first, after: $after) {
        items {
          warehouseID
          warehouseCode
          warehouseName
          address
          warehouseType
          totalCapacity
          currentUtilization
          defaultReceiveLocation
          defaultPickLocation
          active
          totalLocations
          activeLocations
          totalCurrentStock
          createdAt
          modifiedAt
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

// GraphQL query for locations with filters and sorting
const LOCATIONS_QUERY = `
  query GetLocations(
    $first: Int, 
    $after: String, 
    $warehouseCode: String,
    $filter: LocationFilterInput,
    $sort: [LocationSortSortByInput!]
  ) {
    locations {
      locationList(
        first: $first, 
        after: $after, 
        warehouseCode: $warehouseCode,
        filter: $filter,
        sort: $sort
      ) {
        items {
          locationID
          locationCode
          warehouseCode
          warehouseName
          aisle
          rack
          shelf
          bin
          locationType
          capacity
          currentStock
          utilizationPercent
          pickingSequence
          active
          createdAt
          modifiedAt
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

// GraphQL mutation for deactivating a warehouse
const DEACTIVATE_WAREHOUSE_MUTATION = `
  mutation DeactivateWarehouse($id: Int!) {
    warehouses {
      warehouseRecords {
        deactivate(id: $id) {
          __typename
          ... on Succeeded {
            succeeded
          }
        }
      }
    }
  }
`;

// GraphQL mutation for activating a warehouse
const ACTIVATE_WAREHOUSE_MUTATION = `
  mutation ActivateWarehouse($id: Int!) {
    warehouses {
      warehouseRecords {
        activate(id: $id) {
          __typename
          ... on Succeeded {
            succeeded
          }
        }
      }
    }
  }
`;

// GraphQL mutation for creating a location
const CREATE_LOCATION_MUTATION = `
  mutation CreateLocation($location: LocationInput!) {
    locations {
      locationRecords {
        create(location: $location) {
          __typename
          ... on IntEntityId {
            id
          }
        }
      }
    }
  }
`;

// GraphQL mutation for updating a location
const UPDATE_LOCATION_MUTATION = `
  mutation UpdateLocation($id: Int!, $location: LocationInput!) {
    locations {
      locationRecords {
        update(id: $id, location: $location) {
          __typename
          ... on IntEntityId {
            id
          }
        }
      }
    }
  }
`;

// GraphQL mutation for bulk creating locations
const BULK_CREATE_LOCATIONS_MUTATION = `
  mutation BulkCreateLocations($locations: [LocationInput!]!) {
    locations {
      locationRecords {
        bulkCreate(locations: $locations) {
          __typename
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors {
              message
            }
          }
          ... on UnexpectedError {
            errorMessage
          }
        }
      }
    }
  }
`;

// GraphQL mutation for deactivating a location
const DEACTIVATE_LOCATION_MUTATION = `
  mutation DeactivateLocation($id: Int!) {
    locations {
      locationRecords {
        deactivate(id: $id) {
          __typename
          ... on Succeeded {
            succeeded
          }
        }
      }
    }
  }
`;

// GraphQL mutation for blocking a location
const BLOCK_LOCATION_MUTATION = `
  mutation BlockLocation($id: Int!) {
    locations {
      locationRecords {
        block(id: $id) {
          __typename
          ... on Succeeded {
            succeeded
          }
        }
      }
    }
  }
`;

// GraphQL mutation for unblocking a location
const UNBLOCK_LOCATION_MUTATION = `
  mutation UnblockLocation($id: Int!) {
    locations {
      locationRecords {
        unblock(id: $id) {
          __typename
          ... on Succeeded {
            succeeded
          }
        }
      }
    }
  }
`;

// GraphQL mutation for creating a warehouse
const CREATE_WAREHOUSE_MUTATION = `
  mutation CreateWarehouse($warehouse: WarehouseInput!) {
    warehouses {
      warehouseRecords {
        create(warehouse: $warehouse) {
          __typename
          ... on IntEntityId {
            id
          }
        }
      }
    }
  }
`;

// GraphQL mutation for updating a warehouse
const UPDATE_WAREHOUSE_MUTATION = `
  mutation UpdateWarehouse($id: Int!, $warehouse: WarehouseInput!) {
    warehouses {
      warehouseRecords {
        update(id: $id, warehouse: $warehouse) {
          __typename
          ... on IntEntityId {
            id
          }
        }
      }
    }
  }
`;

// GraphQL response types for mutations
interface DeactivateWarehouseGraphQLResponse {
  data?: {
    warehouses?: {
      warehouseRecords?: {
        deactivate?: {
          __typename?: string;
          succeeded?: boolean;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface ActivateWarehouseGraphQLResponse {
  data?: {
    warehouses?: {
      warehouseRecords?: {
        activate?: {
          __typename?: string;
          succeeded?: boolean;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface CreateLocationGraphQLResponse {
  data?: {
    locations?: {
      locationRecords?: {
        create?: {
          __typename?: string;
          id?: number;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface UpdateLocationGraphQLResponse {
  data?: {
    locations?: {
      locationRecords?: {
        update?: {
          __typename?: string;
          id?: number;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface BulkCreateLocationsGraphQLResponse {
  data?: {
    locations?: {
      locationRecords?: {
        bulkCreate?: {
          __typename?: string;
          id?: number;
          errors?: Array<{ message: string }>;
          errorMessage?: string;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface DeactivateLocationGraphQLResponse {
  data?: {
    locations?: {
      locationRecords?: {
        deactivate?: {
          __typename?: string;
          succeeded?: boolean;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface BlockLocationGraphQLResponse {
  data?: {
    locations?: {
      locationRecords?: {
        block?: {
          __typename?: string;
          succeeded?: boolean;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface UnblockLocationGraphQLResponse {
  data?: {
    locations?: {
      locationRecords?: {
        unblock?: {
          __typename?: string;
          succeeded?: boolean;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface CreateWarehouseGraphQLResponse {
  data?: {
    warehouses?: {
      warehouseRecords?: {
        create?: {
          __typename?: string;
          id?: number;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

interface UpdateWarehouseGraphQLResponse {
  data?: {
    warehouses?: {
      warehouseRecords?: {
        update?: {
          __typename?: string;
          id?: number;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

// Location input type for mutations
export interface LocationInput {
  locationCode: string;
  warehouseCode: string;
  aisle?: string;
  rack?: string;
  shelf?: string;
  bin?: string;
  locationType: string;
  capacity?: number;
  pickingSequence?: number;
}

// Warehouse input type for mutations
export interface WarehouseInput {
  warehouseCode: string;
  warehouseName: string;
  address?: string;
  warehouseType: string;
  totalCapacity?: number;
  defaultReceiveLocation?: string;
  defaultPickLocation?: string;
}

// Sort direction type
export type SortDirection = "ASC" | "DESC";

// Sort input for locations - matches backend SortBy<LocationSort> format
export interface LocationSortInput {
  field: "LocationCode" | "LocationType" | "PickingSequence";
  order: "Ascending" | "Descending";
}

// String filter type for text fields - matches backend StringFilter
interface StringFilterInput {
  contains?: string;
  doesNotContain?: string;
  eq?: string;
  ne?: string;
  beginsWith?: string;
  doesNotBeginWith?: string;
  endsWith?: string;
  doesNotEndWith?: string;
  isBlank?: boolean;
  isNotBlank?: boolean;
}

// Filter input for locations - matches backend schema
// Note: Backend uses 'beginsWith' not 'startsWith', 'ne' not 'notEquals', 'isBlank'/'isNotBlank' for empty checks
export interface LocationFilterInput {
  locationCode?: StringFilterInput;
  locationType?: StringFilterInput;
  aisle?: StringFilterInput;
  rack?: StringFilterInput;
  shelf?: StringFilterInput;
  bin?: StringFilterInput;
  active?: { eq?: boolean };
}

export const useWarehousesLocations = () => {
  const { fetchGraphQL } = useGraphQLFetch();

  // Warehouses state
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehousesPageInfo, setWarehousesPageInfo] = useState<PageInfo | null>(null);
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);

  // Locations state
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsPageInfo, setLocationsPageInfo] = useState<PageInfo | null>(null);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Fetch warehouses
  const fetchWarehouses = useCallback(
    async (first: number = 100, after?: string): Promise<{ warehouses: Warehouse[]; pageInfo: PageInfo | null }> => {
      setIsLoadingWarehouses(true);
      setError(null);

      try {
        const result = await fetchGraphQL<WarehousesGraphQLResponse["data"]>(WAREHOUSES_QUERY, { first, after });

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const connection = result.data?.warehouses?.warehouseList;
        const warehouseList = connection?.items?.map((item) => mapWarehouseToFrontend(item)) || [];
        const pageInfo = connection?.pageInfo || null;

        setWarehouses(warehouseList);
        setWarehousesPageInfo(pageInfo);

        return { warehouses: warehouseList, pageInfo };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to fetch warehouses";
        setError(message);
        console.error(err);
        toast.error(`Failed to load warehouses from backend: ${message}`);
        return { warehouses: [], pageInfo: null };
      } finally {
        setIsLoadingWarehouses(false);
      }
    },
    [fetchGraphQL],
  );

  // Fetch locations with filters, sort, and pagination
  const fetchLocations = useCallback(
    async (
      options: {
        first?: number;
        after?: string;
        warehouseCode?: string;
        filter?: LocationFilterInput;
        sort?: LocationSortInput;
      } = {},
    ): Promise<{ locations: Location[]; pageInfo: PageInfo | null }> => {
      setIsLoadingLocations(true);
      setError(null);

      const { first = 100, after, warehouseCode, filter, sort } = options;

      // Convert sort to GraphQL sort format (array of sort objects)
      const sortArray = sort ? [sort] : undefined;

      try {
        const result = await fetchGraphQL<LocationsGraphQLResponse["data"]>(LOCATIONS_QUERY, {
          first,
          after,
          warehouseCode,
          filter,
          sort: sortArray,
        });

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const connection = result.data?.locations?.locationList;
        const locationList = connection?.items?.map((item) => mapLocationToFrontend(item)) || [];
        const pageInfo = connection?.pageInfo || null;

        setLocations(locationList);
        setLocationsPageInfo(pageInfo);

        return { locations: locationList, pageInfo };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to fetch locations";
        setError(message);
        console.error(err);
        toast.error(`Failed to load locations from backend: ${message}`);
        return { locations: [], pageInfo: null };
      } finally {
        setIsLoadingLocations(false);
      }
    },
    [fetchGraphQL],
  );

  // Fetch next page of locations
  const fetchNextLocationsPage = useCallback(
    async (
      options: {
        first?: number;
        warehouseCode?: string;
        filter?: LocationFilterInput;
        sort?: LocationSortInput;
      } = {},
    ): Promise<{ locations: Location[]; pageInfo: PageInfo | null }> => {
      if (!locationsPageInfo?.hasNextPage || !locationsPageInfo?.endCursor) {
        return { locations, pageInfo: locationsPageInfo };
      }

      return fetchLocations({
        ...options,
        after: locationsPageInfo.endCursor,
      });
    },
    [fetchLocations, locations, locationsPageInfo],
  );

  // Deactivate a warehouse
  const deactivateWarehouse = useCallback(
    async (warehouseId: number): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await fetchGraphQL<DeactivateWarehouseGraphQLResponse["data"]>(
          DEACTIVATE_WAREHOUSE_MUTATION,
          { id: warehouseId }
        );

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const deactivateResult = result.data?.warehouses?.warehouseRecords?.deactivate;

        if (deactivateResult?.succeeded) {
          setWarehouses((prev) => prev.map((w) => (w.id === warehouseId ? { ...w, active: false } : w)));
          return { success: true };
        }

        throw new Error("Failed to deactivate warehouse");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to deactivate warehouse";
        setError(message);
        console.error(err);
        return { success: false, error: message };
      }
    },
    [fetchGraphQL],
  );

  // Activate a warehouse
  const activateWarehouse = useCallback(
    async (warehouseId: number): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await fetchGraphQL<ActivateWarehouseGraphQLResponse["data"]>(
          ACTIVATE_WAREHOUSE_MUTATION,
          { id: warehouseId }
        );

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const activateResult = result.data?.warehouses?.warehouseRecords?.activate;

        if (activateResult?.succeeded) {
          setWarehouses((prev) => prev.map((w) => (w.id === warehouseId ? { ...w, active: true } : w)));
          return { success: true };
        }

        throw new Error("Failed to activate warehouse");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to activate warehouse";
        setError(message);
        console.error(err);
        return { success: false, error: message };
      }
    },
    [fetchGraphQL],
  );

  // Create a location
  const createLocation = useCallback(
    async (location: LocationInput): Promise<{ success: boolean; id?: number; error?: string }> => {
      setError(null);

      try {
        const result = await fetchGraphQL<CreateLocationGraphQLResponse["data"]>(
          CREATE_LOCATION_MUTATION,
          { location }
        );

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const createResult = result.data?.locations?.locationRecords?.create;

        if (createResult?.id) {
          return { success: true, id: createResult.id };
        }

        throw new Error("Failed to create location");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to create location";
        setError(message);
        console.error(err);
        return { success: false, error: message };
      }
    },
    [fetchGraphQL],
  );

  // Update a location
  const updateLocation = useCallback(
    async (id: number, location: LocationInput): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await fetchGraphQL<UpdateLocationGraphQLResponse["data"]>(
          UPDATE_LOCATION_MUTATION,
          { id, location }
        );

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const updateResult = result.data?.locations?.locationRecords?.update;

        if (updateResult?.id) {
          return { success: true };
        }

        throw new Error("Failed to update location");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to update location";
        setError(message);
        console.error(err);
        return { success: false, error: message };
      }
    },
    [fetchGraphQL],
  );

  // Bulk create locations
  const bulkCreateLocations = useCallback(
    async (locations: LocationInput[]): Promise<{ success: boolean; count?: number; error?: string }> => {
      setError(null);

      try {
        const result = await fetchGraphQL<BulkCreateLocationsGraphQLResponse["data"]>(
          BULK_CREATE_LOCATIONS_MUTATION,
          { locations }
        );

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const bulkCreateResult = result.data?.locations?.locationRecords?.bulkCreate;
        const typename = bulkCreateResult?.__typename;

        // Check for validation failure
        if (typename === "FailedValidation") {
          const validationErrors = bulkCreateResult?.errors;
          const errorMessages = validationErrors?.map((e) => e.message).join("; ") || "Validation failed";
          throw new Error(errorMessages);
        }

        // Check for unexpected error
        if (typename === "UnexpectedError") {
          const errorMessage = bulkCreateResult?.errorMessage || "An unexpected error occurred";
          throw new Error(errorMessage);
        }

        if (typename === "IntEntityId" && bulkCreateResult?.id !== undefined) {
          return { success: true, count: bulkCreateResult.id };
        }

        throw new Error("Failed to bulk create locations");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to bulk create locations";
        setError(message);
        console.error(err);
        return { success: false, error: message };
      }
    },
    [fetchGraphQL],
  );

  // Deactivate a location
  const deactivateLocation = useCallback(
    async (locationId: number): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await fetchGraphQL<DeactivateLocationGraphQLResponse["data"]>(
          DEACTIVATE_LOCATION_MUTATION,
          { id: locationId }
        );

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const deactivateResult = result.data?.locations?.locationRecords?.deactivate;

        if (deactivateResult?.succeeded) {
          setLocations((prev) => prev.map((l) => (l.id === locationId ? { ...l, active: false } : l)));
          return { success: true };
        }

        throw new Error("Failed to deactivate location");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to deactivate location";
        setError(message);
        console.error(err);
        return { success: false, error: message };
      }
    },
    [fetchGraphQL],
  );

  // Block a location
  const blockLocation = useCallback(
    async (locationId: number): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await fetchGraphQL<BlockLocationGraphQLResponse["data"]>(
          BLOCK_LOCATION_MUTATION,
          { id: locationId }
        );

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const blockResult = result.data?.locations?.locationRecords?.block;

        if (blockResult?.succeeded) {
          return { success: true };
        }

        throw new Error("Failed to block location");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to block location";
        setError(message);
        console.error(err);
        return { success: false, error: message };
      }
    },
    [fetchGraphQL],
  );

  // Unblock a location
  const unblockLocation = useCallback(
    async (locationId: number): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await fetchGraphQL<UnblockLocationGraphQLResponse["data"]>(
          UNBLOCK_LOCATION_MUTATION,
          { id: locationId }
        );

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const unblockResult = result.data?.locations?.locationRecords?.unblock;

        if (unblockResult?.succeeded) {
          return { success: true };
        }

        throw new Error("Failed to unblock location");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to unblock location";
        setError(message);
        console.error(err);
        return { success: false, error: message };
      }
    },
    [fetchGraphQL],
  );

  // Create a warehouse
  const createWarehouse = useCallback(
    async (warehouse: WarehouseInput): Promise<{ success: boolean; id?: number; error?: string }> => {
      setError(null);

      try {
        const result = await fetchGraphQL<CreateWarehouseGraphQLResponse["data"]>(
          CREATE_WAREHOUSE_MUTATION,
          { warehouse }
        );

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const createResult = result.data?.warehouses?.warehouseRecords?.create;

        if (createResult?.id) {
          return { success: true, id: createResult.id };
        }

        throw new Error("Failed to create warehouse");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to create warehouse";
        setError(message);
        console.error(err);
        return { success: false, error: message };
      }
    },
    [fetchGraphQL],
  );

  // Update a warehouse
  const updateWarehouse = useCallback(
    async (id: number, warehouse: WarehouseInput): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await fetchGraphQL<UpdateWarehouseGraphQLResponse["data"]>(
          UPDATE_WAREHOUSE_MUTATION,
          { id, warehouse }
        );

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const updateResult = result.data?.warehouses?.warehouseRecords?.update;

        if (updateResult?.id) {
          return { success: true };
        }

        throw new Error("Failed to update warehouse");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to update warehouse";
        setError(message);
        console.error(err);
        return { success: false, error: message };
      }
    },
    [fetchGraphQL],
  );

  return {
    // Warehouses
    warehouses,
    warehousesPageInfo,
    isLoadingWarehouses,
    fetchWarehouses,
    deactivateWarehouse,
    activateWarehouse,
    createWarehouse,
    updateWarehouse,

    // Locations
    locations,
    locationsPageInfo,
    isLoadingLocations,
    fetchLocations,
    fetchNextLocationsPage,
    createLocation,
    updateLocation,
    bulkCreateLocations,
    deactivateLocation,
    blockLocation,
    unblockLocation,

    // General
    error,
  };
};
